import * as React from "react";
import { School } from "Stores/superStore/schools";
import {
    CellContext,
    Column,
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Row,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import { Button, Form, Table } from "react-bootstrap";
import {SchoolForm} from "./components/school";
import {Fragment, useMemo, useState} from "react";
import DebouncedInput from "Components/Common/DebouncedInput";
import {isIngestibleFilter} from "../../../common/filters";
import PageTitle from "Components/Common/PageTitle";
import {useInternalContext} from "@/contexts/internal";
import {useSchoolBasic} from "@/hooks/schools";

export function SchoolsList() {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [columnFilters, setColumnFilers] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const {setRenderActionButton} = useInternalContext();
    const {data: schools, mutate} = useSchoolBasic();

    const columns = useMemo<ColumnDef<School>[]>(
        () => [
            {
                header: "Ingestible",
                accessorKey: "ingestible",
                filterFn: isIngestibleFilter,
                cell: (props) => (
                    <div>
                        { props.row.original.ingestible ? <i className="icon icon-check topaz-icon" aria-hidden="true"/> : null }
                    </div>),
            },
            {
                header: "Name",
                accessorKey: "display_name",
            },
            {
                header: "ID",
                accessorKey: "school_id",
            },
            {
                header: "School Type",
                accessorKey: "school_type",
            },
            {
                header: "Max Users",
                accessorKey: "max_number_users",
            },
            {
                id: "expander",
                header: () => null,
                cell: (props: CellContext<School, unknown>) => {
                    return (
                        <button className="table-action-button" onClick={ props.row.getToggleExpandedHandler() }>
                            <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`} style={{display: "inline-block", verticalAlign: "baseline"}}/>
                        </button>);
                },
            }
        ], []);

    const table = useReactTable({
        columns,
        data: schools ?? [],
        state: {
            columnFilters,
            sorting
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        onColumnFiltersChange: setColumnFilers,
        onSortingChange: setSorting,
    });

    const handleClose = () => {
        setShowCreateForm(false);
    };

    const handleCreateClick = () => {
        setShowCreateForm(true);
    };

    const renderCreateButton = () => {
        return (
            <div className="actions">
                <Button onClick={ handleCreateClick }>create school</Button>
            </div>
        );
    };

    const renderSubComponent = ({row}: {row: Row<School>}) => {
        return (
            <SchoolForm
                schoolId={row.original.school_id}
                onSave={async () => {
                    row.toggleExpanded();
                    await mutate();
                }}
                onClose={() => row.toggleExpanded()} />
        );
    };

    const renderTable = () => {
        return (
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : (
                                        <>
                                            <div
                                                className={header.column.getCanSort() ? "cursor-pointer " + (header.column.getIsSorted() || "") : ""}
                                                onClick={header.column.getToggleSortingHandler()}>
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </div>
                                            {(header.column.getCanFilter() && (
                                                <div>
                                                    <Filter column={header.column} />
                                                </div>) || <div className="fill-no-filter"></div>)}
                                        </>
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <Fragment key={row.id}>
                        <tr>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                        {row.getIsExpanded() && (
                            <tr>
                                <td colSpan={row.getVisibleCells().length}>
                                    {renderSubComponent({ row })}
                                </td>
                            </tr>
                        )}
                    </Fragment>
                ))}
                </tbody>
            </Table>
        );
    };

    React.useEffect(() => {
        setRenderActionButton(renderCreateButton());

        return () => {
            setRenderActionButton(null); // Clean up
        };
    }, []);


    return (
        <div className="list list-container show-filters">
            <PageTitle pageTitle={"Schools Management"} />
            {showCreateForm
                ? <SchoolForm onClose={handleClose} onSave={async () => {
                    handleClose();
                    await mutate();
                }} />
                : renderTable()}
        </div>
    );
}

interface FilterProps {
    column: Column<any, unknown>
}
function Filter({column}: FilterProps) {
    const columnFilterValue = column.getFilterValue();

    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        column.setFilterValue(() => e.target.value);
    };

    return column.id === "ingestible" ? (
        <Form.Select aria-label="Any" onChange={onChange}>
            <option value={"any"}>Any</option>
            <option value={"yes"}>Yes</option>
            <option value={"no"}>No</option>
        </Form.Select>
    ) : (
        <DebouncedInput value={(columnFilterValue ?? "") as string} onChange={value => column.setFilterValue(value)} />
    );
}